import * as React from "react"
import { ImageOff } from "lucide-react"
import { cn } from "@/lib/utils"

type Thumb = { id: number; url: string | null }

// Cycles through a clip's thumbnails on a fixed interval (~1.2s). Renders a
// "no thumbnail yet" placeholder when the clip has none — used as the empty
// state for an open ClipTodo whose clip hasn't been recorded yet.
//
// Used by the Script tab visuals layer and the /todos page Clip TODOs list.
export function CyclingThumbnail({
  thumbnails,
  title,
  className,
  intervalMs = 1200,
  emptyLabel = "No thumbnail yet",
}: {
  thumbnails: Thumb[]
  title: string
  className?: string
  intervalMs?: number
  emptyLabel?: string | null
}) {
  const usable = thumbnails.filter((t) => !!t.url)
  const [index, setIndex] = React.useState(0)
  React.useEffect(() => {
    if (usable.length <= 1) return
    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % usable.length)
    }, intervalMs)
    return () => window.clearInterval(id)
  }, [usable.length, intervalMs])

  if (usable.length === 0) {
    return (
      <div className={cn(
        "flex aspect-video w-full items-center justify-center gap-2 rounded border border-dashed border-hairline bg-page text-xs text-ink-muted",
        className,
      )}>
        <ImageOff className="h-4 w-4" />
        {emptyLabel && <span>{emptyLabel}</span>}
      </div>
    )
  }

  const current = usable[index]
  return (
    <div className={cn(
      "relative aspect-video w-full overflow-hidden rounded border border-hairline bg-black",
      className,
    )}>
      {current?.url && (
        <img
          src={current.url}
          alt={`${title} thumbnail ${index + 1}`}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      )}
      {usable.length > 1 && (
        <div className="absolute bottom-1 right-1 flex gap-0.5 rounded bg-black/60 px-1 py-0.5">
          {usable.map((t, i) => (
            <span
              key={t.id}
              className={cn("h-1 w-1 rounded-full", i === index ? "bg-white" : "bg-white/40")}
            />
          ))}
        </div>
      )}
    </div>
  )
}
